import * as Sentry from "@sentry/react";
import { excludeGraphQLFetch } from "apollo-link-sentry";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

import envConfig from "#/config/env.ts";
import { User } from "#/typesConstants/user.ts";

export const setupSentry = () => {
  if (!envConfig.sentry.dsn) {
    return;
  }

  Sentry.init({
    dsn: envConfig.sentry.dsn,
    environment: envConfig.sentry.env || "development",
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        traceFetch: false,
      }),
    ],
    beforeBreadcrumb: excludeGraphQLFetch,
    ignoreErrors: [
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource.",
      'can\'t redefine non-configurable property "get"',
      "File already uploaded",
    ],

    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
};

export const captureException = (error: unknown) => {
  if (envConfig.sentry.dsn) {
    Sentry.captureException(error);
  }
};

export const captureMessage = (message: string) => {
  if (envConfig.sentry.dsn) {
    Sentry.captureMessage(message, "error");
  } else {
    console.error(message);
  }
};

export function captureFetchError(message: string, response: Response) {
  if (envConfig.sentry.dsn) {
    Sentry.withScope((scope) => {
      scope.setExtra("response_status", response.status);
      scope.setExtra("url", response.url);

      const error = new Error(message);
      // avoids having a generic 'Error' as the error name.
      error.name = "FetchError";
      Sentry.captureException(error);
    });
  }
}

export function setUser({
  id,
  email,
  firstName,
  lastName,
  role,
  company,
}: User) {
  if (envConfig.sentry.dsn) {
    Sentry.setUser({
      id,
      email,
      username: `${firstName} ${lastName}`,
      role,
      company,
    });
  }
}
